import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authService } from '../../services/auth.service';
import { attendanceService } from '../../services/attendance.service';
import { taskService } from '../../services/task.service';
import { leaveService } from '../../services/leave.service';
import { FaCalendarAlt, FaTasks, FaFileAlt, FaClock } from 'react-icons/fa';

const Home = () => {
  const user = authService.getCurrentUser();
  const [stats, setStats] = useState({
    todayAttendance: null,
    pendingTasks: 0,
    upcomingLeaves: 0,
    documents: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [attendanceData, tasksData, leavesData] = await Promise.all([
        attendanceService.getMyAttendance({ limit: 1 }).catch(() => ({ attendance: [] })),
        taskService.getMyTasks().catch(() => ({ tasks: [] })),
        leaveService.getMyLeaves().catch(() => ({ leaves: [] }))
      ]);

      // Check today's attendance
      const today = attendanceData.attendance?.find(a => 
        new Date(a.date).toDateString() === new Date().toDateString()
      );

      // Count pending tasks
      const pendingTasks = tasksData.tasks?.filter(t => t.status === 'pending' || t.status === 'in_progress').length || 0;

      // Count upcoming leaves
      const upcomingLeaves = leavesData.leaves?.filter(l => 
        l.status === 'approved' && new Date(l.startDate) >= new Date()
      ).length || 0;

      setStats({
        todayAttendance: today,
        pendingTasks,
        upcomingLeaves,
        documents: 0 // Can be fetched if needed
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickCheckIn = async () => {
    try {
      await attendanceService.checkIn({});
      toast.success('Checked in successfully!');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to check in');
    }
  };

  const handleQuickCheckOut = async () => {
    try {
      await attendanceService.checkOut({});
      toast.success('Checked out successfully!');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to check out');
    }
  };

  return (
    <div className="p-6">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">
          Welcome back, {user?.name}!
        </h1>
        <p className="text-gray-600 mt-2">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {!stats.todayAttendance ? (
                <button
                  onClick={handleQuickCheckIn}
                  className="flex flex-col items-center p-4 bg-green-50 border-2 border-green-200 rounded-lg hover:bg-green-100 transition-all"
                >
                  <FaClock className="text-3xl text-green-600 mb-2" />
                  <span className="text-sm font-medium">Check In</span>
                </button>
              ) : !stats.todayAttendance.checkOut ? (
                <button
                  onClick={handleQuickCheckOut}
                  className="flex flex-col items-center p-4 bg-red-50 border-2 border-red-200 rounded-lg hover:bg-red-100 transition-all"
                >
                  <FaClock className="text-3xl text-red-600 mb-2" />
                  <span className="text-sm font-medium">Check Out</span>
                </button>
              ) : (
                <div className="flex flex-col items-center p-4 bg-gray-50 border-2 border-gray-200 rounded-lg">
                  <FaClock className="text-3xl text-gray-400 mb-2" />
                  <span className="text-sm font-medium text-gray-600">Attendance Done</span>
                </div>
              )}

              <Link
                to="/dashboard/tasks"
                className="flex flex-col items-center p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
              >
                <FaTasks className="text-3xl text-blue-600 mb-2" />
                <span className="text-sm font-medium">My Tasks</span>
              </Link>

              <Link
                to="/dashboard/leaves"
                className="flex flex-col items-center p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
              >
                <FaCalendarAlt className="text-3xl text-blue-600 mb-2" />
                <span className="text-sm font-medium">Apply Leave</span>
              </Link>

              <Link
                to="/dashboard/documents"
                className="flex flex-col items-center p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
              >
                <FaFileAlt className="text-3xl text-blue-600 mb-2" />
                <span className="text-sm font-medium">Documents</span>
              </Link>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Today's Status</p>
                  <p className="text-2xl font-bold text-green-600 mt-2">
                    {stats.todayAttendance ? (
                      stats.todayAttendance.checkOut ? 'Completed' : 'Checked In'
                    ) : 'Not Checked In'}
                  </p>
                  {stats.todayAttendance?.checkIn && (
                    <p className="text-xs text-gray-500 mt-1">
                      In: {new Date(stats.todayAttendance.checkIn).toLocaleTimeString()}
                    </p>
                  )}
                </div>
                <FaClock className="text-4xl text-green-200" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Pending Tasks</p>
                  <p className="text-2xl font-bold text-blue-600 mt-2">{stats.pendingTasks}</p>
                </div>
                <FaTasks className="text-4xl text-blue-200" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Upcoming Leaves</p>
                  <p className="text-2xl font-bold text-purple-600 mt-2">{stats.upcomingLeaves}</p>
                </div>
                <FaCalendarAlt className="text-4xl text-purple-200" />
              </div>
            </div>
          </div>

          {/* Today's Attendance Card */}
          {stats.todayAttendance && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Today's Attendance</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Check In</p>
                  <p className="text-lg font-semibold">
                    {new Date(stats.todayAttendance.checkIn).toLocaleTimeString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Check Out</p>
                  <p className="text-lg font-semibold">
                    {stats.todayAttendance.checkOut 
                      ? new Date(stats.todayAttendance.checkOut).toLocaleTimeString() 
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Hours Worked</p>
                  <p className="text-lg font-semibold">
                    {stats.todayAttendance.totalHours 
                      ? `${stats.todayAttendance.totalHours.toFixed(2)}h` 
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span className={`inline-block px-3 py-1 text-sm rounded ${
                    stats.todayAttendance.status === 'present' ? 'bg-green-100 text-green-800' :
                    stats.todayAttendance.status === 'late' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {stats.todayAttendance.status}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Home;